import * as React from 'react';
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { HiSparkles } from 'react-icons/hi2';
import { IoArrowRedoOutline } from 'react-icons/io5';
import Modal from 'components/Modal/HeadlessUiModal';
import { useModal } from 'hooks/useModal';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { trackEvent } from 'services/client/analytics';
import { FLOW_AI_ANALYSIS_OPENED } from 'services/client/analytics/events';
import { SIGNUP_PAGE } from 'constants/pages';
import Link from 'components/Link';
import { useDynamicScrollSection } from 'hooks/useDynamicScrollSection';
import FlowAiAnalysisSidebar from 'layouts/FlowPageContainer/FlowAiAnalysisSidebar';
import FlowAiAnalysisHeader from 'layouts/FlowPageContainer/FlowAiAnalysisHeader';
import FlowAiAnalysisGuide from 'layouts/FlowPageContainer/FlowAiAnalysisGuide';
import FlowAiAnalysisReport, {
  FLOW_ANALYSIS_REPORT_SECTIONS,
  FlowAnalysisSectionId,
} from 'layouts/FlowPageContainer/FlowAiAnalysisReport';
import useFlowAiAnalysis from 'layouts/FlowPageContainer/useFlowAiAnalysis';

const IS_ENABLED =
  true || (typeof window !== 'undefined' && window.location.href.includes('ai-analysis'));
const IS_FANCY_BUTTON = false;

interface FlowAiAnalysisModalProps {
  className?: string;
  isPageLoading?: boolean;
  putContracts?: number;
  callContracts?: number;
  putPremium?: number;
  callPremium?: number;
  flowSideLabel?: string;
  bullishPremium?: number;
  bearishPremium?: number;
  neutralPremium?: number;
  showVisualizations?: boolean;
}

interface SectionHeading {
  id: string;
  title: string;
  level: number;
}

// Stays until the reader opens the new report; the button carries the screen-reader text.
const NewBadge = () => {
  return (
    <span
      aria-hidden="true"
      style={{ fontSize: '10px' }}
      className="pointer-events-none absolute -right-5 -top-1.5 animate-badge-in rounded-full bg-gradient-to-r from-green-500 to-teal-500 px-2 py-0.5 text-xs font-medium text-white shadow-lg"
    >
      New
    </span>
  );
};

const NewReportLabel = () => <span className="sr-only">, new report available</span>;

const FlowAiAnalysisModal: React.FC<FlowAiAnalysisModalProps> = ({
  className = '',
  isPageLoading = false,
  putContracts = 0,
  callContracts = 0,
  putPremium = 0,
  callPremium = 0,
  flowSideLabel = 'Ask',
  bullishPremium = 0,
  bearishPremium = 0,
  neutralPremium = 0,
  showVisualizations = true,
}) => {
  const { isOpen, openModal, closeModal } = useModal();
  const closeButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const infoButtonRef = React.useRef<HTMLButtonElement>(null);
  const guideHeadingRef = React.useRef<HTMLHeadingElement>(null);
  const guideId = React.useId();
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [guideDestination, setGuideDestination] = useState<FlowAnalysisSectionId | null>(null);
  const [hasAnimated, setHasAnimated] = useState(false);
  const { analysis, isRefreshing, isError, showNewBadge, refresh } = useFlowAiAnalysis(
    isPageLoading,
    isOpen,
  );
  const markdown = analysis?.markdown ?? null;
  // The same run as typed fields; without it the markdown renders as before.
  const report = analysis?.report ?? null;
  const lastUpdated = analysis?.lastUpdated ?? null;
  const isLoading = isRefreshing && !markdown;
  const [sectionHeadings, setSectionHeadings] = useState<SectionHeading[]>([]);
  const [scrollContainer, setScrollContainer] = useState<HTMLElement | null>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const { user } = useCurrentUser();
  const isLoggedIn = !!user;

  React.useEffect(() => {
    if (isOpen && isGuideOpen) guideHeadingRef.current?.focus({ preventScroll: true });
  }, [isOpen, isGuideOpen]);

  const backToAnalysis = () => {
    setGuideDestination(null);
    setIsGuideOpen(false);
    infoButtonRef.current?.focus({ preventScroll: true });
  };

  React.useEffect(() => {
    if (isOpen && markdown && !isLoading && sectionHeadings.length > 0) {
      const timer = setTimeout(() => {
        if (contentRef.current) {
          setScrollContainer(contentRef.current);
        }
      }, 100);
      return () => clearTimeout(timer);
    }

    if (!isOpen) {
      setScrollContainer(null);
    }
  }, [isOpen, markdown, isLoading, sectionHeadings]);

  const { activeSection, scrollToSection } = useDynamicScrollSection({
    rootEl: scrollContainer,
    sectionIds: sectionHeadings.map((h) => h.id),
  });

  const handleTabChange = (tabId: string) => {
    scrollToSection(tabId);
  };

  // Wait until the mounted report is visible before measuring its section position.
  React.useEffect(() => {
    if (!isOpen || isGuideOpen || !guideDestination || !scrollContainer) return;
    const heading = scrollContainer.querySelector<HTMLElement>(`#section-${guideDestination}`);
    if (heading) {
      scrollToSection(guideDestination);
      heading.focus({ preventScroll: true });
    }
    setGuideDestination(null);
  }, [isOpen, isGuideOpen, guideDestination, scrollContainer, scrollToSection]);

  const parseMarkdownHeadings = React.useCallback((markdownText: string) => {
    const headingRegex = /^(#{2,3})\s+(.+)$/gm;
    const headings: SectionHeading[] = [];
    let match;

    while ((match = headingRegex.exec(markdownText)) !== null) {
      const level = match[1].length;
      const title = match[2].trim();
      const id = title
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-');

      headings.push({ id, title, level });
    }

    return headings;
  }, []);

  React.useEffect(() => {
    if (report) {
      setSectionHeadings(
        FLOW_ANALYSIS_REPORT_SECTIONS.map(({ id, title }) => ({ id, title, level: 3 })),
      );
    } else if (markdown) {
      const headings = parseMarkdownHeadings(markdown);
      setSectionHeadings(headings);
    }
  }, [markdown, report, parseMarkdownHeadings]);

  React.useEffect(() => {
    // Wait for page to finish loading, then delay animation by 1 second
    // Animation runs twice (2 seconds each = 4 seconds total), then stops
    if (!isPageLoading && !hasAnimated) {
      const timer = setTimeout(() => {
        setHasAnimated(true);
      }, 5000); // 1 second delay + 4 seconds for two sweeps
      return () => clearTimeout(timer);
    }
  }, [isPageLoading, hasAnimated]);

  const handleOpenModal = () => {
    trackEvent(FLOW_AI_ANALYSIS_OPENED, {});
    setGuideDestination(null);
    setIsGuideOpen(false);
    openModal();
    // Reopening also revalidates a previously loaded report or retries a failed first load.
    void refresh();
  };

  React.useEffect(() => {
    if (!isOpen) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [isOpen, refresh]);

  if (!IS_ENABLED) {
    return null;
  }

  return (
    <>
      {IS_FANCY_BUTTON ? (
        <div className="relative">
          <button
            className={`group relative ml-2 flex items-center gap-1.5 overflow-hidden rounded-md bg-gradient-to-r from-blue-500 via-blue-500 to-cyan-500 px-3 py-1.5 text-sm font-semibold text-white shadow-lg transition-all duration-300 hover:bg-blue-600 hover:bg-none ${className}`}
            onClick={handleOpenModal}
            title="AI Market Analysis"
          >
            {/* Initial beam sweep effect - plays twice */}
            {!hasAnimated && (
              <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-md">
                <span
                  className="beam-sweep absolute top-0 h-full w-[150%]"
                  style={{
                    background:
                      'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.9) 50%, transparent 100%)',
                  }}
                />
              </span>
            )}

            {/* Moving border effect on hover */}
            <span className="absolute inset-0 rounded-md opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              <span className="absolute -inset-1 animate-[spin_3s_linear_infinite] rounded-md bg-gradient-to-r from-teal-400 via-cyan-500 to-blue-500 opacity-75 blur-sm" />
            </span>

            {/* Content */}
            <span className="relative z-10 flex items-center gap-1.5">
              <HiSparkles className="h-3.5 w-3.5" />
              <span className="text-sm font-semibold" style={{ fontSize: '12px' }}>
                AI Flow Analysis
              </span>
              {showNewBadge && <NewReportLabel />}
            </span>
          </button>

          {/* New badge for fancy button */}
          {showNewBadge && <NewBadge />}
        </div>
      ) : (
        <div className="relative">
          <button
            className={`ml-2 flex h-7 items-center gap-1.5 rounded bg-white px-3 text-sm font-semibold text-brand-primary transition-colors hover:bg-brand-primary hover:text-white ${className}`}
            onClick={handleOpenModal}
            title="AI Market Analysis"
          >
            <HiSparkles className="h-3.5 w-3.5" />
            <span style={{ fontSize: '12px' }}>AI Flow Analysis</span>
            {showNewBadge && <NewReportLabel />}
          </button>

          {/* New badge for simple button */}
          {showNewBadge && <NewBadge />}
        </div>
      )}

      <style jsx>{`
        @keyframes sweep {
          0% {
            transform: translateX(-100%);
            opacity: 1;
          }
          95% {
            transform: translateX(100%);
            opacity: 1;
          }
          100% {
            transform: translateX(100%);
            opacity: 0;
          }
        }

        .beam-sweep {
          animation: sweep 2s linear 2;
          transform: translateX(-100%);
          opacity: 0;
        }
      `}</style>

      <Modal
        isOpen={isOpen}
        ariaLabel="AI Market Analysis"
        initialFocus={closeButtonRef}
        handleClose={closeModal}
        classNameMaxWidth="max-w-4xl"
        classNameHeight="h-auto"
        className="border-palette-border bg-palette-black-3 [color-scheme:dark] sm:border"
        classNameRounded="rounded-none sm:rounded-2xl"
        overlayClassName="bg-black/60 backdrop-blur-sm"
      >
        <div
          className="flex h-[calc(100dvh-env(safe-area-inset-bottom,0px))] max-h-[100dvh] flex-col bg-palette-black-3 text-white sm:h-[min(840px,calc(100dvh-48px-env(safe-area-inset-bottom,0px)))]"
          onKeyDownCapture={(event) => {
            // Keep Escape within the guide. The next Escape closes the parent dialog.
            if (isGuideOpen && event.key === 'Escape') {
              event.preventDefault();
              event.stopPropagation();
              backToAnalysis();
            }
          }}
        >
          <div className="safearea-spacer-top flex-shrink-0 bg-palette-black-3" />
          {/* Header */}
          <FlowAiAnalysisHeader
            lastUpdated={lastUpdated}
            isLoading={isLoading}
            onClose={closeModal}
            onAbout={() => (isGuideOpen ? backToAnalysis() : setIsGuideOpen(true))}
            isGuideOpen={isGuideOpen}
            guideId={guideId}
            infoButtonRef={infoButtonRef}
            closeButtonRef={closeButtonRef}
          />
          {isGuideOpen && (
            <FlowAiAnalysisGuide
              id={guideId}
              onBack={backToAnalysis}
              headingRef={guideHeadingRef}
              report={isLoggedIn ? report : null}
              availableSections={isLoggedIn ? sectionHeadings.map(({ id }) => id) : []}
              onViewSection={(id) => {
                setGuideDestination(id);
                setIsGuideOpen(false);
              }}
            />
          )}
          {/* Content */}
          <div
            hidden={isGuideOpen}
            className={isGuideOpen ? 'hidden' : 'flex min-h-0 flex-1'}
            data-analysis-report
          >
            {markdown && (
              <div className="hidden md:block">
                <FlowAiAnalysisSidebar
                  activeTab={activeSection}
                  onTabChange={handleTabChange}
                  sections={sectionHeadings.map((h) => ({
                    id: h.id,
                    label: h.title,
                    level: h.level,
                  }))}
                  putContracts={putContracts}
                  callContracts={callContracts}
                  putPremium={putPremium}
                  callPremium={callPremium}
                  flowSideLabel={flowSideLabel}
                  bullishPremium={bullishPremium}
                  bearishPremium={bearishPremium}
                  neutralPremium={neutralPremium}
                  showVisualizations={showVisualizations}
                />
              </div>
            )}
            <div
              ref={contentRef}
              className={`relative min-h-0 flex-1 px-6 py-6 ${
                isLoggedIn ? 'overflow-y-auto' : 'overflow-hidden'
              }`}
            >
              <div>
                {isLoading && (
                  <div className="flex h-full items-center justify-center">
                    <div className="text-center">
                      <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-brand-green-primary border-t-transparent"></div>
                      <p className="text-gray-300">Loading the latest read…</p>
                    </div>
                  </div>
                )}

                {isError && !isLoading && !markdown && (
                  <div className="flex h-full items-center justify-center">
                    <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-6 text-center">
                      {/* The request result cannot distinguish "no stored report" from a failure. */}
                      <p className="text-red-400">The analysis didn&apos;t load.</p>
                      <button
                        type="button"
                        onClick={() => void refresh()}
                        className="mt-3 rounded px-3 py-1.5 text-sm text-white underline underline-offset-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue"
                      >
                        Reload
                      </button>
                    </div>
                  </div>
                )}

                {report && <FlowAiAnalysisReport report={report} />}

                {markdown && !report && (
                  <div className="prose prose-invert max-w-none">
                    <ReactMarkdown
                      components={{
                        h1: ({ children, ...props }) => {
                          const text = String(children);
                          const id = text
                            .toLowerCase()
                            .replace(/[^\w\s-]/g, '')
                            .replace(/\s+/g, '-');
                          return (
                            <h1
                              {...props}
                              id={`section-${id}`}
                              className="mb-4 text-3xl font-bold text-white"
                            >
                              {children}
                            </h1>
                          );
                        },
                        h2: ({ children, ...props }) => {
                          const text = String(children);
                          const id = text
                            .toLowerCase()
                            .replace(/[^\w\s-]/g, '')
                            .replace(/\s+/g, '-');
                          return (
                            <h2
                              {...props}
                              id={`section-${id}`}
                              tabIndex={-1}
                              className="mb-3 mt-6 text-2xl font-semibold text-white outline-none"
                            >
                              {children}
                            </h2>
                          );
                        },
                        h3: ({ children, ...props }) => {
                          const text = String(children);
                          const id = text
                            .toLowerCase()
                            .replace(/[^\w\s-]/g, '')
                            .replace(/\s+/g, '-');
                          return (
                            <h3
                              {...props}
                              id={`section-${id}`}
                              tabIndex={-1}
                              className="mb-2 mt-4 text-xl font-semibold text-white outline-none"
                            >
                              {children}
                            </h3>
                          );
                        },
                        p: ({ children, ...props }) => (
                          <p {...props} className="mb-4 leading-relaxed text-gray-300">
                            {children}
                          </p>
                        ),
                        ul: ({ children, ...props }) => (
                          <ul {...props} className="mb-4 list-disc space-y-2 pl-6 text-gray-300">
                            {children}
                          </ul>
                        ),
                        ol: ({ children, ...props }) => (
                          <ol {...props} className="mb-4 list-decimal space-y-2 pl-6 text-gray-300">
                            {children}
                          </ol>
                        ),
                        li: ({ children, ...props }) => (
                          <li {...props} className="text-gray-300">
                            {children}
                          </li>
                        ),
                        strong: ({ children, ...props }) => (
                          <strong {...props} className="font-semibold text-white">
                            {children}
                          </strong>
                        ),
                        em: ({ children, ...props }) => (
                          <em {...props} className="italic text-gray-400">
                            {children}
                          </em>
                        ),
                        code: ({ children, ...props }) => (
                          <code
                            {...props}
                            className="rounded bg-palette-black-1 px-1.5 py-0.5 font-mono text-sm text-brand-green-primary"
                          >
                            {children}
                          </code>
                        ),
                        blockquote: ({ children, ...props }) => (
                          <blockquote
                            {...props}
                            className="border-l-4 border-brand-green-primary pl-4 italic text-gray-400"
                          >
                            {children}
                          </blockquote>
                        ),
                      }}
                    >
                      {markdown}
                    </ReactMarkdown>
                  </div>
                )}
              </div>

              {/* Gradient overlay with CTA for non-logged-in users */}
              {!isLoggedIn && markdown && (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 flex h-2/3 items-end justify-center bg-gradient-to-t from-palette-black-3 via-palette-black-3/90 to-transparent pb-2">
                  <div className="pointer-events-auto flex flex-col items-center gap-3 text-center">
                    <p className="max-w-md text-sm text-gray-300">
                      The full report is free. <strong>Market themes</strong>,{' '}
                      <strong>hourly shifts</strong>,{' '}
                      <strong>the biggest trades on the tape</strong>, and{' '}
                      <strong>setups ready for the calculator</strong>.
                    </p>
                    <Link
                      href={SIGNUP_PAGE}
                      className="hover:bg-brand-primary-hover flex items-center gap-2 rounded-md bg-brand-primary px-6 py-2.5 text-sm font-semibold text-white transition-colors"
                    >
                      <span>Get Free Access</span>
                      <IoArrowRedoOutline className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default FlowAiAnalysisModal;
